import { Modal } from "antd";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  isCreditsPricing,
  type OrganizationUserDto,
  type AiQuotaPaywallReason,
} from "@superblocksteam/shared";

import { GauInText } from "core/billing";
import { getCurrentUser } from "legacy/selectors/usersSelectors";
import {
  selectOnlyOrganization,
  selectOnlyOrganizationId,
} from "store/slices/organizations";
import { useListBuilderSeatsQuery } from "store/slices/reduxApi/builderSeats";
import { useListUsersQuery } from "store/slices/reduxApi/users";

type ContactAdminBillingModalProps = {
  open: boolean;
  onClose: () => void;
  currentUserHasBuilderSeat?: boolean | null;
  reason?: AiQuotaPaywallReason;
};

const ORG_ROLE_OWNER = "Owner";
const ORG_ROLE_ADMIN = "Admin";
function getOrgAdminOrOwnerEmail(users: OrganizationUserDto[]): string | null {
  const withRole = (roleName: string) =>
    users.find((u) => u.assignments?.some((a) => a.role?.name === roleName));
  const owner = withRole(ORG_ROLE_OWNER);
  if (owner?.email) return owner.email;
  const admin = withRole(ORG_ROLE_ADMIN);
  return admin?.email ?? null;
}

export function ContactAdminBillingModal({
  open,
  onClose,
  currentUserHasBuilderSeat: currentUserHasBuilderSeatProp,
  reason,
}: ContactAdminBillingModalProps) {
  const organizationId = useSelector(selectOnlyOrganizationId);
  const organization = useSelector(selectOnlyOrganization);
  const currentUser = useSelector(getCurrentUser);
  // Seatless (credits-pricing) orgs have no seat concept: skip the seat
  // fetch and never show "Request AI Builder seat" copy — the GAU-adjustment
  // variant is the only one that makes sense.
  const isSeatless = isCreditsPricing(organization?.billing?.pricingModel);
  const { data: users } = useListUsersQuery(
    { organizationId: organizationId ?? "" },
    { skip: !open || !organizationId },
  );
  const { data: builderSeatsData } = useListBuilderSeatsQuery(undefined, {
    skip: !open || isSeatless,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const adminEmail = useMemo(
    () => getOrgAdminOrOwnerEmail(users ?? []),
    [users],
  );
  const currentUserHasBuilderSeat = useMemo(() => {
    if (isSeatless) {
      return true;
    }
    if (typeof currentUserHasBuilderSeatProp === "boolean") {
      return currentUserHasBuilderSeatProp;
    }

    if (!currentUser || !builderSeatsData?.builders) {
      return null;
    }

    const normalizedEmail = currentUser.email.trim().toLowerCase();
    return builderSeatsData.builders.some(
      (builder) =>
        builder.userId === currentUser.id ||
        builder.email.trim().toLowerCase() === normalizedEmail,
    );
  }, [
    builderSeatsData?.builders,
    currentUser,
    currentUserHasBuilderSeatProp,
    isSeatless,
  ]);
  const isDeployQuota = reason === "deploy_quota_exceeded";
  const isPastDue = reason === "payment_past_due";
  const modalTitle = isPastDue ? (
    "Subscription payment failed"
  ) : isDeployQuota ? (
    "Deploy limit reached"
  ) : currentUserHasBuilderSeat ? (
    <GauInText>Request GAU adjustment</GauInText>
  ) : (
    "Request AI Builder seat"
  );
  const modalBody = isPastDue ? (
    "Your organization's most recent subscription payment didn't go through, so paid features are paused. You don't have permission to manage billing — ask an organization admin to update the payment method and pay the outstanding invoice."
  ) : isDeployQuota ? (
    "You do not have permission to manage billing for your organization. Ask an organization admin to add more deployed apps."
  ) : currentUserHasBuilderSeat ? (
    <GauInText>
      You do not have permission to manage billing for your organization. Ask an
      organization admin to adjust GAUs for you.
    </GauInText>
  ) : (
    "You do not have permission to manage billing for your organization. Ask an organization admin to assign you an AI Builder seat"
  );
  const contactAdminSubject = isPastDue
    ? "Subscription payment failed — billing action needed"
    : isDeployQuota
      ? "Request additional deployed apps"
      : currentUserHasBuilderSeat
        ? "Request GAU adjustment"
        : "Request AI Builder seat or GAU adjustment";
  const contactAdminBody = isPastDue
    ? "Hi,\n\nOur Superblocks subscription is currently past due — the most recent payment didn't go through and AI features are paused. Could you open the Stripe billing portal to update the payment method and pay the outstanding invoice?\n\nThanks."
    : isDeployQuota
      ? "Hi,\n\nCould you please add more deployed apps to our plan?\n\nThanks."
      : currentUserHasBuilderSeat
        ? "Hi,\n\nCould you please adjust our GAUs for me?\n\nThanks."
        : "Hi,\n\nCould you please purchase AI Builder seats or adjust our GAUs for me?\n\nThanks.";

  const openContactAdmin = () => {
    const mailto = `mailto:${encodeURIComponent(adminEmail ?? "")}?subject=${encodeURIComponent(contactAdminSubject)}&body=${encodeURIComponent(contactAdminBody)}`;
    window.open(mailto, "_blank", "noreferrer");
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
      closable={false}
      className="tailwind"
      modalRender={(modal) => (
        <div className="overflow-hidden rounded-[8px]">{modal}</div>
      )}
      bodyStyle={{ padding: 0 }}
    >
      <div className="flex flex-col bg-white">
        <div className="flex h-12 items-center justify-between border-b border-gray-200 pl-6 pr-[10px]">
          <h2 className="!m-0 text-[16px] font-semibold leading-6 text-gray-950">
            {modalTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-50 hover:text-gray-700"
          >
            <span className="text-[24px] font-light leading-none">×</span>
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="m-0 text-[14px] leading-6 text-[#44505f]">
            {modalBody}
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 transition hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={openContactAdmin}
            className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md bg-black px-5 text-sm font-medium !text-white transition hover:bg-black/90"
          >
            Contact admin
          </button>
        </div>
      </div>
    </Modal>
  );
}
